import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  approveRecipientName,
  correctRecipientName,
  listErrors,
  resetOrderStatus,
} from "@/api/endpoints";
import { ApiError } from "@/api/client";

export default function ErrorsPage() {
  const [days, setDays] = useState(7);
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["errors", days],
    queryFn: () => listErrors(days, 200),
    refetchInterval: 60_000,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["errors"] });
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const resetMut = useMutation({
    mutationFn: (orderId: string) => resetOrderStatus(orderId, "new"),
    onSuccess: () => {
      invalidateAll();
      setActionError(null);
    },
    onError: (e) => {
      setActionError(e instanceof ApiError ? e.message : String(e));
    },
  });

  // 受取人名レビュー: 発行結果 (issue_result) にエラーがあれば表示する
  const handleNameActionResult = (r: {
    order_no: string;
    issue_result: Record<string, unknown> | null;
  }) => {
    invalidateAll();
    const err = r.issue_result?.error;
    if (err) {
      setActionInfo(null);
      setActionError(
        `注文 ${r.order_no}: 承認は完了しましたがラベル発行に失敗しました: ${String(err)}`,
      );
    } else {
      setActionError(null);
      setActionInfo(
        `注文 ${r.order_no}: ラベル発行が完了しました (tracking: ${String(
          r.issue_result?.tracking_no ?? "-",
        )})`,
      );
    }
  };

  const approveMut = useMutation({
    mutationFn: (orderId: string) => approveRecipientName(orderId, true),
    onSuccess: handleNameActionResult,
    onError: (e) => {
      setActionInfo(null);
      setActionError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const correctMut = useMutation({
    mutationFn: (v: { orderId: string; name: string }) =>
      correctRecipientName(v.orderId, v.name, true),
    onSuccess: handleNameActionResult,
    onError: (e) => {
      setActionInfo(null);
      setActionError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-brand-primary">エラー一覧</h2>
          <p className="text-sm text-gray-500 mt-1">
            エラー状態の受注、ラベル発行の失敗、発送完了の失敗を確認できます。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">表示期間:</label>
          <select
            className="input w-24"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={1}>1日</option>
            <option value={7}>7日</option>
            <option value={30}>30日</option>
            <option value={90}>90日</option>
          </select>
          <button
            className="btn-secondary"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
          >
            {q.isFetching ? "更新中…" : "更新"}
          </button>
        </div>
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          {actionError}
        </div>
      )}
      {actionInfo && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded">
          {actionInfo}
        </div>
      )}

      {q.isLoading && <p className="text-gray-500">読み込み中…</p>}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          取得失敗: {String(q.error)}
        </div>
      )}

      {q.data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="stat-card">
              <span className="text-sm text-gray-500">エラー状態の受注</span>
              <span
                className={`text-3xl font-bold mt-1 ${
                  q.data.summary.error_orders > 0
                    ? "text-brand-alert"
                    : "text-gray-400"
                }`}
              >
                {q.data.summary.error_orders}
              </span>
            </div>
            <div className="stat-card">
              <span className="text-sm text-gray-500">ラベル発行失敗</span>
              <span
                className={`text-3xl font-bold mt-1 ${
                  q.data.summary.failed_label_issuances > 0
                    ? "text-brand-alert"
                    : "text-gray-400"
                }`}
              >
                {q.data.summary.failed_label_issuances}
              </span>
            </div>
            <div className="stat-card">
              <span className="text-sm text-gray-500">発送完了失敗</span>
              <span
                className={`text-3xl font-bold mt-1 ${
                  q.data.summary.failed_fulfillments > 0
                    ? "text-brand-alert"
                    : "text-gray-400"
                }`}
              >
                {q.data.summary.failed_fulfillments}
              </span>
            </div>
          </div>

          <section>
            <h3 className="text-lg font-semibold mb-3">
              エラー状態の受注 ({q.data.error_orders.length})
            </h3>
            <div className="card overflow-x-auto">
              {q.data.error_orders.length === 0 ? (
                <p className="text-center text-gray-400 py-4">
                  エラー状態の受注はありません 🎉
                </p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="border-b text-left text-gray-500">
                    <tr>
                      <th className="py-2 pr-3">プラットフォーム</th>
                      <th className="py-2 pr-3">注文番号</th>
                      <th className="py-2 pr-3">受取人</th>
                      <th className="py-2 pr-3">最終更新</th>
                      <th className="py-2">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.error_orders.map((o) => (
                      <tr key={o.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 pr-3">
                          {o.platform}
                          <span className="ml-1 text-xs text-gray-500">/{o.country_code}</span>
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs">{o.order_no}</td>
                        <td className="py-2 pr-3 max-w-xs">
                          <span className="truncate block">{o.recipient_name}</span>
                          {o.name_suspect_reason && (
                            <span className="block text-xs text-amber-600 mt-0.5">
                              ⚠ {o.name_suspect_reason}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-500">
                          {o.updated_at
                            ? new Date(o.updated_at).toLocaleString("ja-JP")
                            : "-"}
                        </td>
                        <td className="py-2">
                          {o.name_suspect_reason ? (
                            <div className="flex flex-col gap-1">
                              <button
                                className="btn-accent text-xs"
                                disabled={approveMut.isPending || correctMut.isPending}
                                onClick={() => {
                                  if (
                                    confirm(
                                      `注文 ${o.order_no} の受取人名「${o.recipient_name}」を承認してこのままラベル発行します。よろしいですか？\n\n(台湾の通関実名認証は個人名が必須です)`,
                                    )
                                  ) {
                                    approveMut.mutate(o.id);
                                  }
                                }}
                              >
                                この名前で発行
                              </button>
                              <button
                                className="btn-secondary text-xs"
                                disabled={approveMut.isPending || correctMut.isPending}
                                onClick={() => {
                                  const name = prompt(
                                    `注文 ${o.order_no} の正しい受取人名 (購入者に確認した本名) を入力してください:`,
                                    o.recipient_name,
                                  );
                                  if (name && name.trim()) {
                                    correctMut.mutate({
                                      orderId: o.id,
                                      name: name.trim(),
                                    });
                                  }
                                }}
                              >
                                名前を修正して発行
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn-accent text-xs"
                              disabled={resetMut.isPending}
                              onClick={() => {
                                if (
                                  confirm(
                                    `注文 ${o.order_no} のステータスを 'new' に戻します。よろしいですか？`,
                                  )
                                ) {
                                  resetMut.mutate(o.id);
                                }
                              }}
                            >
                              ステータスをリセット (→ new)
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">
              ラベル発行失敗履歴 ({q.data.failed_label_issuances.length})
            </h3>
            <div className="card overflow-x-auto">
              {q.data.failed_label_issuances.length === 0 ? (
                <p className="text-center text-gray-400 py-4">
                  失敗したラベル発行はありません 🎉
                </p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="border-b text-left text-gray-500">
                    <tr>
                      <th className="py-2 pr-3">プラットフォーム</th>
                      <th className="py-2 pr-3">注文番号</th>
                      <th className="py-2 pr-3">エラーコード</th>
                      <th className="py-2 pr-3">試行時刻</th>
                      <th className="py-2">エラー内容</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.failed_label_issuances.map((f) => (
                      <tr key={f.log_id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 pr-3">
                          {f.platform}
                          {f.logistics_type && (
                            <span className="ml-1 text-xs text-gray-500">
                              /{f.logistics_type}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs">{f.order_no}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-gray-600">
                          {f.error_code ?? "-"}
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-500">
                          {f.attempted_at
                            ? new Date(f.attempted_at).toLocaleString("ja-JP")
                            : "-"}
                        </td>
                        <td className="py-2 text-xs text-red-700 max-w-md truncate" title={f.error_message ?? ""}>
                          {f.error_message ?? "-"}
                          {f.request_id && (
                            <span className="block text-gray-400 font-mono">
                              req: {f.request_id}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">
              発送完了失敗履歴 ({q.data.failed_fulfillments.length})
            </h3>
            <div className="card overflow-x-auto">
              {q.data.failed_fulfillments.length === 0 ? (
                <p className="text-center text-gray-400 py-4">
                  失敗した発送完了はありません 🎉
                </p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="border-b text-left text-gray-500">
                    <tr>
                      <th className="py-2 pr-3">プラットフォーム</th>
                      <th className="py-2 pr-3">注文番号</th>
                      <th className="py-2 pr-3 text-right">再試行回数</th>
                      <th className="py-2 pr-3">送信時刻</th>
                      <th className="py-2">エラー内容</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.failed_fulfillments.map((f) => (
                      <tr key={f.log_id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 pr-3">{f.platform}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{f.order_no}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.retry_count}</td>
                        <td className="py-2 pr-3 text-xs text-gray-500">
                          {f.sent_at ? new Date(f.sent_at).toLocaleString("ja-JP") : "-"}
                        </td>
                        <td className="py-2 text-xs text-red-700 max-w-md truncate">
                          {f.error_message ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
